// Precondition: ft-button and ft-multi-select-buttons must be loaded
// Postcondition: Custom element for filtering tables with search and category buttons

import { icon } from "../../../shared/icons.js";

interface FilterState {
  searchQuery: string;
  activeCategories: Set<string>;
}

interface MultiSelectButtonsElement extends HTMLElement {
  setSelected(values: string[]): void;
  selectAll(): void;
  getSelected(): string[];
}

class TableFilter extends HTMLElement {
  private filterState: FilterState = {
    searchQuery: "",
    activeCategories: new Set(["new", "increase", "decrease", "exited"]),
  };
  private abortController = new AbortController();
  private isExpanded = false;

  static get observedAttributes() {
    return ["categories", "placeholder", "counts"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string,
    newValue: string,
  ): void {
    if (oldValue !== newValue && this.shadowRoot?.children.length) {
      this.render();
    }
  }

  disconnectedCallback(): void {
    this.abortController.abort();
  }

  // Public API: Get current filter state
  getFilterState(): FilterState {
    return {
      searchQuery: this.filterState.searchQuery,
      activeCategories: new Set(this.filterState.activeCategories),
    };
  }

  // Public API: Update filter state programmatically
  setSearchQuery(query: string): void {
    this.filterState.searchQuery = query;
    const input = this.shadowRoot?.querySelector(
      ".search-input",
    ) as HTMLInputElement;
    if (input) {
      input.value = query;
    }
    this.dispatchFilterChange();
  }

  // Public API: Update active categories
  setActiveCategories(categories: string[]): void {
    this.filterState.activeCategories = new Set(categories);
    const multiSelect = this.shadowRoot?.querySelector(
      "ft-multi-select-buttons",
    ) as MultiSelectButtonsElement | null;
    multiSelect?.setSelected(categories);
    this.dispatchFilterChange();
  }

  // Public API: Reset filter state
  reset(): void {
    const categories = this.getCategories();
    this.filterState.searchQuery = "";
    this.filterState.activeCategories = new Set(categories);

    const input = this.shadowRoot?.querySelector(
      ".search-input",
    ) as HTMLInputElement;
    if (input) {
      input.value = "";
    }

    const multiSelect = this.shadowRoot?.querySelector(
      "ft-multi-select-buttons",
    ) as MultiSelectButtonsElement | null;
    multiSelect?.selectAll();

    this.dispatchFilterChange();
  }

  private getCategories(): string[] {
    return (
      this.getAttribute("categories")
        ?.split(",")
        .map((c) => c.trim()) || ["new", "increase", "decrease", "exited"]
    );
  }

  private dispatchFilterChange(): void {
    this.dispatchEvent(
      new CustomEvent("filter-change", {
        detail: this.getFilterState(),
        bubbles: true,
        composed: true,
      }),
    );
  }

  private setupEventListeners(): void {
    this.abortController.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    const searchInput = this.shadowRoot?.querySelector(
      ".search-input",
    ) as HTMLInputElement;
    const settingsBtn = this.shadowRoot?.querySelector(
      ".settings-btn",
    ) as HTMLElement;
    const filterControls = this.shadowRoot?.querySelector(".filter-controls");
    const multiSelect = this.shadowRoot?.querySelector(
      "ft-multi-select-buttons",
    );

    // Settings button click to toggle expand/collapse
    settingsBtn?.addEventListener(
      "button-click",
      () => {
        this.isExpanded = !this.isExpanded;
        if (this.isExpanded) {
          filterControls?.classList.add("visible");
          searchInput?.focus();
        } else {
          filterControls?.classList.remove("visible");
        }
      },
      { signal },
    );

    // Search input listener
    searchInput?.addEventListener(
      "input",
      (e) => {
        this.filterState.searchQuery = (e.target as HTMLInputElement).value;
        this.dispatchFilterChange();
      },
      { signal },
    );

    // Multi-select change listener
    multiSelect?.addEventListener(
      "change",
      (e) => {
        const selected = (e as CustomEvent).detail.selected as string[];
        this.filterState.activeCategories = new Set(selected);
        this.dispatchFilterChange();
      },
      { signal },
    );
  }

  private render(): void {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(this.getStyles());

    const template = document.createElement("template");
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = "";
      this.shadowRoot.adoptedStyleSheets = [sheet];
      this.shadowRoot.appendChild(template.content.cloneNode(true));
      this.setupEventListeners();
    }
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
        font-family: var(--font-sans, Inter, system-ui, sans-serif);
      }

      * {
        box-sizing: border-box;
      }

      button, input {
        font-family: inherit;
        font-size: inherit;
        line-height: inherit;
        color: inherit;
        margin: 0;
        padding: 0;
        background: transparent;
        border: none;
      }

      button {
        cursor: pointer;
        user-select: none;
      }

      .filter-bar {
        height: 2rem;
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }

      .filter-controls {
        display: none;
        align-items: center;
        gap: 8px;
        flex: 1 1 auto;
        flex-wrap: wrap;
      }

      .filter-controls.visible {
        display: flex;
      }

      .search-input {
        box-sizing: border-box;
        flex: 1 1 auto;
        min-width: 180px;
        height: var(--height-sm, 32px);
        padding: 0 12px;
        font: 400 13px/1 var(--font-sans, Inter, system-ui, sans-serif);
        background: var(--input-bg, #ffffff);
        color: var(--text, #1C1C1E);
        border: 1px solid var(--input-border, #E2E4E8);
        border-radius: var(--radius, 8px);
        outline: none;
        transition: border-color 0.15s ease;
      }

      .search-input:focus {
        border-color: var(--input-border-focus, #999999);
      }

      .search-input::placeholder {
        color: var(--input-placeholder, #999999);
      }

      .settings-btn {
        flex-shrink: 0;
      }

      @media (prefers-color-scheme: dark) {
        .search-input {
          background: var(--input-bg, #14161a);
          color: var(--text, #ffffff);
          border-color: var(--input-border, #272a31);
        }

        .search-input:focus {
          border-color: var(--input-border-focus, #444444);
        }

        .search-input::placeholder {
          color: var(--input-placeholder, #8a8a8a);
        }
      }
    `;
  }

  private getCounts(): Record<string, number> {
    const countsAttr = this.getAttribute("counts");
    if (!countsAttr) return {};
    try {
      return JSON.parse(countsAttr);
    } catch (err) {
      console.warn(
        "ft-table-filter: invalid counts attribute:",
        countsAttr,
        err,
      );
      return {};
    }
  }

  private getTemplate(): string {
    const categories = this.getCategories();
    const counts = this.getCounts();
    const placeholder =
      this.getAttribute("placeholder") || "Filter by name or CUSIP";

    const options = categories.map((cat) => ({
      value: cat,
      label: cat.charAt(0).toUpperCase() + cat.slice(1),
      count: counts[cat],
    }));

    return `
      <div class="filter-bar">
        <ft-button class="settings-btn" variant="icon" type="secondary" size="small" label="Filter options">
          ${icon("sliders", 20)}
        </ft-button>

        <div class="filter-controls">
          <input type="text" class="search-input" placeholder="${placeholder}">
          <ft-multi-select-buttons
            size="small"
            options='${JSON.stringify(options)}'
            selected="${categories.join(",")}"
          ></ft-multi-select-buttons>
        </div>
      </div>
    `;
  }
}

// Register custom element
if (!customElements.get("ft-table-filter")) {
  customElements.define("ft-table-filter", TableFilter);
}
